import JSZip from "jszip";
import { normalizeLyricsText } from "./normalizeLyricsText";

const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

type LyricsFormat = "text" | "rtf" | "docx" | "doc";

function decodeBytes(bytes: Uint8Array, encoding: "utf-8" | "utf-16le"): string {
  if (typeof TextDecoder !== "undefined") {
    return new TextDecoder(encoding, { fatal: false }).decode(bytes);
  }

  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes).toString(encoding === "utf-16le" ? "utf16le" : "utf8");
  }

  return "";
}

function getExtension(filename: string): string {
  const base = filename.split("?")[0].split("/").pop() || "";
  const dot = base.lastIndexOf(".");
  return dot >= 0 ? base.slice(dot + 1).toLowerCase() : "";
}

function detectLyricsFormat(filename: string, bytes: Uint8Array): LyricsFormat {
  if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) {
    return "docx";
  }

  if (
    bytes.length >= 4 &&
    bytes[0] === 0xd0 &&
    bytes[1] === 0xcf &&
    bytes[2] === 0x11 &&
    bytes[3] === 0xe0
  ) {
    return "doc";
  }

  const head = decodeBytes(bytes.slice(0, 8), "utf-8");
  if (head.startsWith("{\\rtf")) {
    return "rtf";
  }

  const ext = getExtension(filename);
  if (ext === "rtf") return "rtf";
  if (ext === "docx" || ext === "doc") return "docx";
  return "text";
}

function walkDocxNode(node: Element, parts: string[]): void {
  const localName = node.localName;
  if (localName === "t") {
    parts.push(node.textContent ?? "");
    return;
  }
  if (localName === "tab") {
    parts.push("\t");
    return;
  }
  if (localName === "br" || localName === "cr") {
    parts.push("\n");
    return;
  }

  for (let i = 0; i < node.children.length; i += 1) {
    walkDocxNode(node.children[i] as Element, parts);
  }
}

export function extractTextFromDocxXml(xml: string): string {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const body = doc.getElementsByTagNameNS(W_NS, "body")[0];
  if (!body) return "";

  const paragraphs = body.getElementsByTagNameNS(W_NS, "p");
  const lines: string[] = [];

  for (let i = 0; i < paragraphs.length; i += 1) {
    const parts: string[] = [];
    const paragraph = paragraphs[i];
    for (let j = 0; j < paragraph.children.length; j += 1) {
      walkDocxNode(paragraph.children[j] as Element, parts);
    }
    lines.push(parts.join("").replace(/\s+$/u, ""));
  }

  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

async function parseDocxBuffer(buffer: ArrayBuffer): Promise<string> {
  const zip = await JSZip.loadAsync(buffer);
  const documentXml =
    zip.file("word/document.xml") ??
    zip.file(/word\/document\.xml$/i)?.[0] ??
    null;

  if (!documentXml) return "";

  const xml = await documentXml.async("string");
  return extractTextFromDocxXml(xml);
}

function extractTextFromLegacyDoc(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunks: string[] = [];
  let current = "";

  for (let i = 0; i < bytes.length - 1; i += 2) {
    const code = bytes[i] | (bytes[i + 1] << 8);
    const isPrintable =
      code === 0x0a ||
      code === 0x0d ||
      code === 0x09 ||
      (code >= 0x20 && code < 0xd800) ||
      (code >= 0xe000 && code <= 0xfffd);

    if (isPrintable) {
      current += String.fromCharCode(code);
    } else if (current.trim().length >= 3) {
      chunks.push(current.trim());
      current = "";
    } else {
      current = "";
    }
  }

  if (current.trim().length >= 3) {
    chunks.push(current.trim());
  }

  const lyricsLike = chunks.filter((chunk) => {
    if (chunk.length < 3) return false;
    if (/^[\x00-\x1f\x7f-\x9f]+$/.test(chunk)) return false;
    if (/^(Microsoft|Word|Document|Normal|Times New Roman|Calibri)/i.test(chunk)) {
      return false;
    }
    return /[\p{L}\p{N}]/u.test(chunk);
  });

  return lyricsLike.join("\n").trim();
}

function decodeTextBuffer(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const utf8 = decodeBytes(bytes, "utf-8");
  if (!utf8.includes("\uFFFD")) {
    return utf8;
  }
  return decodeBytes(bytes, "utf-16le");
}

export async function parseLyricsBuffer(
  buffer: ArrayBuffer,
  filename = ""
): Promise<string> {
  const bytes = new Uint8Array(buffer);
  const format = detectLyricsFormat(filename, bytes);

  if (format === "docx") {
    const text = await parseDocxBuffer(buffer);
    if (text) return text;
  }

  if (format === "doc") {
    const legacy = extractTextFromLegacyDoc(buffer);
    if (legacy) return legacy;
  }

  const raw = decodeTextBuffer(buffer).trim();
  if (format === "rtf" || getExtension(filename) === "rtf" || raw.startsWith("{\\rtf")) {
    return normalizeLyricsText(raw);
  }

  return raw;
}

function isLyricsFile(source: unknown): source is File {
  return (
    typeof source === "object" &&
    source !== null &&
    "arrayBuffer" in source &&
    typeof (source as File).arrayBuffer === "function" &&
    "name" in source &&
    typeof (source as File).name === "string"
  );
}

export async function fetchLyricsContent(
  source: string | File
): Promise<string> {
  try {
    if (isLyricsFile(source)) {
      const buffer = await source.arrayBuffer();
      const text = await parseLyricsBuffer(buffer, source.name);
      return normalizeLyricsText(text);
    }

    if (typeof source === "string" && source.trim()) {
      const res = await fetch(source);
      if (!res.ok) return "";

      const buffer = await res.arrayBuffer();
      const filename = source.split("?")[0].split("/").pop() || "";
      const text = await parseLyricsBuffer(buffer, filename);
      return normalizeLyricsText(text);
    }
  } catch {
    return "";
  }

  return "";
}
